import React, { useMemo, useState } from 'react';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { appAgentService } from '../../../services/central-api/app-agent';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { Modal, ScrollView, TouchableOpacity, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { RecordNotLatestValues } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import ConflictUpdateModal from '../../../components/ConflictUpdateModal';
import MyRadio from '../../../components/MyRadio';
import IncrementDecrementInput from '../../../components/IncrementDecrementInput';
import InvolvedPeople from '../../../components/app-agent/InvolvedPeople';

interface AgentOccurrenceDisplaceQuestionaryParams {
    occurrence: Occurrence;
    involvedPeople: InvolvedPerson[];
    setInvolvedPeople: React.Dispatch<React.SetStateAction<InvolvedPerson[]>>;
    involvedObjects: InvolvedObject[];
    involvedVehicles: InvolvedVehicle[];
    involved: number;
    setInvolved: React.Dispatch<React.SetStateAction<number>>;
    displaceReason: DisplaceReason | undefined;
    setStep: React.Dispatch<React.SetStateAction<'occurrenceDetail' | 'occurrenceReport' | 'occurrenceInvolved' | 'displaceQuestionary' | 'policeReport'>>;
    getOccurrence: (occurrenceId: string) => Promise<void>;
    navigation: Navigation;
    shouldFinishOccurrence: boolean;
    setShouldFinishOccurrence: React.Dispatch<React.SetStateAction<boolean>>;
    agentUnit: { id: number; displaced: boolean; OccurrenceUnit: OccurrenceUnit; } | undefined;
    handcuffed: number;
    setHandcuffed: React.Dispatch<React.SetStateAction<number>>;
}

export default function AgentOccurrenceDisplaceQuestionary({
    occurrence,
    involvedPeople,
    setInvolvedPeople,
    involvedObjects,
    involvedVehicles,
    involved,
    setInvolved,
    displaceReason,
    setStep,
    getOccurrence,
    navigation,
    shouldFinishOccurrence,
    setShouldFinishOccurrence,
    agentUnit,
    handcuffed,
    setHandcuffed,
}: AgentOccurrenceDisplaceQuestionaryParams) {
    const { styles, theme } = useStyles(styleSheet);

    const [arrested, setArrested] = useState(0);
    const [prisonHappened, setPrisonHappened] = useState<'yes' | 'no' | null>(null);
    const [handcuffHappened, setHandcuffHappened] = useState<'yes' | 'no' | null>(null);

    const [conflictModalVisible, setConflictModalVisible] = useState(false);
    const [conflictUpdateUser, setConflictUpdateUser] = useState<string>('');
    const [isScreenVisible, setIsScreenVisible] = useState<boolean>(true);

    const [originalInvolvedPeople, setOriginalInvolvedPeople] = useState<InvolvedPerson[]>(involvedPeople);

    async function updateOccurrenceInvolved() {
        if (!occurrence?.id) {
            return;
        }

        try {
            await appAgentService.updateOccurrence({
                id: occurrence.id,
                involvedVehicles: involvedVehicles.map(involvedVehicle => involvedVehicle.id && involvedVehicle.id < 0 ? { ...involvedVehicle, id: undefined } : involvedVehicle),
                involvedPeople: involvedPeople.map(involvedPerson => involvedPerson.id && involvedPerson.id < 0 ? { ...involvedPerson, id: undefined } : involvedPerson),
                involvedObjects: involvedObjects.map(involvedObject => involvedObject.id && involvedObject.id < 0 ? { ...involvedObject, id: undefined } : involvedObject),
                updatedAt: occurrence.updatedAt
            });

            await getOccurrence(String(occurrence.id));

            setOriginalInvolvedPeople(involvedPeople);
            setInvolved(involvedPeople.length);

            if (shouldFinishOccurrence) {
                navigation.navigate('AgentOccurrenceDetails', { occurrenceId: occurrence.id });
            }
        } catch (err) {
            console.error(err);
            if (err instanceof RecordNotLatestValues) {
                setConflictModalVisible(true);
                setConflictUpdateUser(err.userName);
                return;
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    const checkDisabled = useMemo(() => {
        const involvedPeopleArrested = involvedPeople.filter(ip => ip.arrested == true).length;
        const involvedPeopleHandcuffed = involvedPeople.filter(ip => ip.handcuffUsed == true).length;

        if (agentUnit && agentUnit.displaced && agentUnit.OccurrenceUnit.displaceReasonId) {

            if (displaceReason?.category == 'arrested') {
                if (prisonHappened === null) {
                    return true;
                }

                if (involvedPeopleArrested && prisonHappened != 'yes') {
                    return true;
                }

                if (!involvedPeopleArrested && prisonHappened == 'yes') {
                    return true;
                }

                if (arrested != involvedPeopleArrested) {
                    return true;
                }
            }

            if (involved != involvedPeople.length || involved == 0) {
                return true;
            }
        }

        if (handcuffHappened === null) {
            return true;
        }

        if (involvedPeopleHandcuffed && handcuffHappened != 'yes') {
            return true;
        }

        if (!involvedPeopleHandcuffed && handcuffHappened == 'yes') {
            return true;
        }

        if (handcuffed != involvedPeopleHandcuffed) {
            return true;
        }

        return false;
    }, [arrested, involvedPeople, agentUnit, involved, displaceReason, prisonHappened, handcuffed, handcuffHappened]);

    return (
        <>
            <Modal
                visible={isScreenVisible}
                onRequestClose={() => {
                    setIsScreenVisible(false);
                }}
                transparent={true}
            >
                <View style={{ marginTop: 64, flex: 1, flexDirection: 'column', backgroundColor: theme.colors.paginationBar.background }}>
                    <ScrollView contentContainerStyle={{ flex: 1 }}>
                        <View style={[styles.container, { justifyContent: 'space-between' }]}>
                            <View style={{ gap: 25 }}>
                                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <TouchableOpacity onPress={() => {
                                        setIsScreenVisible(false);
                                        setStep('occurrenceDetail');
                                    }}>
                                        <FontAwesomeIcon fontSize={30} icon={faArrowLeft} color={theme.colors.iconColor} />
                                    </TouchableOpacity>
                                </View>
                                {agentUnit && agentUnit.displaced && agentUnit.OccurrenceUnit.displaceReasonId
                                    ? <MyAppText style={{ fontSize: 20 }}>{translate('drivingTo')} {displaceReason?.name}</MyAppText>
                                    : null
                                }
                                {(
                                    involved != involvedPeople.length &&
                                    (displaceReason?.category != 'arrested' ||
                                        (displaceReason?.category == 'arrested' && prisonHappened !== null))
                                ) || (arrested != involvedPeople.filter(ip => ip.arrested == true).length &&
                                    displaceReason?.category == 'arrested' && prisonHappened == 'yes')
                                    ? <View style={{ backgroundColor: '#FCD3A3', flexDirection: 'row', alignItems: 'center', padding: 5, borderRadius: 4, gap: 5 }}>
                                        <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#B8401B' }} />
                                        <MyAppText style={{ fontSize: 12, color: '#000000' }}>
                                            {involved != involvedPeople.length ? translate('involvedNotMatch') : translate('arrestedNotMatch')}
                                        </MyAppText>
                                    </View> : null
                                }
                                {(handcuffed != involvedPeople.filter(ip => ip.handcuffUsed == true).length &&
                                    handcuffHappened == 'yes')
                                    ? <View style={{ backgroundColor: '#FCD3A3', flexDirection: 'row', alignItems: 'center', padding: 5, borderRadius: 4, gap: 5 }}>
                                        <FontAwesomeIcon icon={faExclamationCircle} style={{ color: '#B8401B' }} />
                                        <MyAppText style={{ fontSize: 12, color: '#000000' }}>
                                            {translate('handcuffNotMatch')}
                                        </MyAppText>
                                    </View> : null
                                }
                                {agentUnit && agentUnit.displaced && agentUnit.OccurrenceUnit.displaceReasonId
                                    ? <>
                                        <MyAppText style={{ fontSize: 16 }}>{translate('arrestedInvestigatedText')}</MyAppText>
                                        <IncrementDecrementInput value={involved} setValue={setInvolved} />
                                        {displaceReason?.category == 'arrested' ?
                                            <View style={{ flexDirection: 'row', gap: 20, alignItems: 'flex-end' }}>
                                                <MyAppText style={{ fontSize: 16 }}>{translate('prisonHappened')}?</MyAppText>
                                                <MyRadio
                                                    selected={prisonHappened}
                                                    setSelected={(value: 'yes' | 'no') => {
                                                        setPrisonHappened(value);
                                                        if (value === 'no') {
                                                            setArrested(0);
                                                        }
                                                    }}
                                                    options={[
                                                        { label: translate('yes'), value: 'yes' },
                                                        { label: translate('no'), value: 'no' },
                                                    ]}
                                                />
                                            </View> : null
                                        }
                                    </> : null
                                }
                                {displaceReason?.category == 'arrested' && prisonHappened == 'yes' ?
                                    <>
                                        <MyAppText>{translate('arrestedAmountQuestion')}</MyAppText>
                                        <IncrementDecrementInput value={arrested} maxValue={involved} setValue={setArrested} />
                                    </> : null}

                                <View style={{ flexDirection: 'row', gap: 20, alignItems: 'flex-end' }}>
                                    <MyAppText style={{ fontSize: 16 }}>{translate('handcuffHappened')}?</MyAppText>
                                    <MyRadio
                                        selected={handcuffHappened}
                                        setSelected={(value: 'yes' | 'no') => {
                                            setHandcuffHappened(value);

                                            if (value == 'no') {
                                                setHandcuffed(0);
                                            }
                                        }}
                                        options={[
                                            { label: translate('yes'), value: 'yes' },
                                            { label: translate('no'), value: 'no' },
                                        ]}
                                    />
                                </View>

                                {handcuffHappened == 'yes' ?
                                    <>
                                        <MyAppText>{translate('handcuffAmountQuestion')}</MyAppText>
                                        <IncrementDecrementInput value={handcuffed} maxValue={involved} setValue={setHandcuffed} />
                                    </> : null}

                                {(involved > 0 && displaceReason?.category != 'arrested') || (involved > 0 && prisonHappened == 'no') || (involved > 0 && prisonHappened == 'yes' && arrested > 0) ?
                                    <View>
                                        <InvolvedPeople
                                            involvedPeople={involvedPeople}
                                            setInvolvedPeople={setInvolvedPeople}
                                            updateOccurrenceInvolved={updateOccurrenceInvolved}
                                        />
                                    </View> : null}
                            </View>
                            <TouchableOpacity style={[styles.finishButton, checkDisabled ? styles.disabledConcludeButton : undefined]}
                                onPress={async () => {
                                    setShouldFinishOccurrence(true);
                                    await updateOccurrenceInvolved();
                                    setIsScreenVisible(false);
                                    setStep('occurrenceReport');
                                }} disabled={checkDisabled}>
                                <MyAppText style={{ color: checkDisabled ? theme.colors.primaryButton.disabledText : '#FF0027', fontSize: 16 }}>{translate('finish').toUpperCase()} </MyAppText>
                            </TouchableOpacity>
                        </View>
                    </ScrollView>
                </View>
            </Modal>
            <ConflictUpdateModal
                setModalVisible={setConflictModalVisible}
                isModalVisible={conflictModalVisible}
                onSubmit={() => navigation.navigate('AgentOccurrenceDetails', { occurrenceId: occurrence.id })}
                userName={conflictUpdateUser}
                onCancel={() => {
                    setConflictUpdateUser('');
                    setInvolvedPeople(originalInvolvedPeople);
                }}
            />
        </>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        padding: 16,
        gap: 20
    },
    loading: {
        marginHorizontal: 'auto',
        transform: [{ scaleX: 2 }, { scaleY: 2 }],
        width: '20%',
        height: '20%',
    },
    loadingText: {
        color: theme.colors.loadingColor,
        textAlign: 'center',
        fontSize: 16
    },
    loadingView: {
        flex: 1,
        justifyContent: 'center',
        alignContent: 'center'
    },
    finishButton: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: '#FF0027',
        padding: 14,
        display: 'flex',
        alignItems: 'center',
    },
    disabledConcludeButton: {
        borderWidth: 0,
        backgroundColor: theme.colors.primaryButton.disabledBackground
    },
}));
